import {
  type ElementType,
  type MouseEvent,
  type ReactNode,
  useState,
} from "react";
import { useSelector } from "react-redux";
import { Link, type NavigateFunction, useNavigate } from "react-router-dom";
import AutoAwesomeOutlinedIcon from "@mui/icons-material/AutoAwesomeOutlined";
import DataUsageIcon from "@mui/icons-material/DataUsage";
import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";
import HubOutlinedIcon from "@mui/icons-material/HubOutlined";
import LockPersonIcon from "@mui/icons-material/LockPerson";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import {
  AppBar,
  Avatar,
  Box,
  IconButton,
  SvgIcon,
  Toolbar,
  Tooltip,
} from "@mui/material";
import queryString from "query-string";

import ProfileMenu from "@/apps/admin/components/ProfileMenu/ProfileMenu";
import { paths } from "@/apps/admin/configurations/paths";
import { OBJECT_NAME } from "@/apps/admin/enums/objectName";
import { OBJECT_TYPE } from "@/apps/admin/enums/objectType";
import { checkGlobalManagementRolePermission } from "@/apps/admin/utilities/data";
import { ReactComponent as TSCLogoText } from "@/common/assets/vectors/tsc_logo_text.svg";
import { ASK_GENIE_RESIZING_ATTRIBUTE } from "@/common/genie/dock/genieDockLayout";
import { default as QuickAccessMenu } from "@/ui/components/QuickAccessMenu/QuickAccessMenu";
import { VerticalDivider } from "@/ui/components/VerticalDivider/VerticalDivider";

import {
  selectPermissions,
  selectUser,
} from "../../features/authentication/authenticationSlice";

export const topMenuHeight = "64px";

/**
 * `genieLauncher`: the Genie button, owned by GlobalLayout with the dialog it
 * opens. `rightInset`: room left on the right for a docked Genie, so the bar
 * and its buttons stay beside it.
 */
type QuickAccessOption = {
  title: string;
  icon: ElementType;
  onClick: () => void;
  disabled?: boolean;
};

type TopNavProps = {
  genieLauncher?: ReactNode;
  rightInset?: string;
};

const TopNav = ({ genieLauncher = null, rightInset = "0px" }: TopNavProps) => {
  const navigate = useNavigate();
  const user = useSelector(selectUser);
  const [profileMenuAnchor, setProfileMenuAnchor] =
    useState<HTMLElement | null>(null);
  const profileMenuOpen = Boolean(profileMenuAnchor);
  const handleOpenProfileMenu = (event: MouseEvent<HTMLElement>) =>
    setProfileMenuAnchor(event.currentTarget);
  const handleCloseProfileMenu = () => setProfileMenuAnchor(null);
  const userPermissions = useSelector(selectPermissions);

  const quickaccessOptions = buildQuickAccessOptions(userPermissions, navigate);

  if (!user) return null;
  return (
    <AppBar
      position="fixed"
      sx={{
        backgroundColor: "white",
        zIndex: (theme) => theme.zIndex.drawer + 1,
        width: "auto",
        left: 0,
        right: rightInset,
        transition: (theme) =>
          theme.transitions.create("right", {
            easing: theme.transitions.easing.easeIn,
            duration: theme.transitions.duration.leavingScreen,
          }),
        [`html[${ASK_GENIE_RESIZING_ATTRIBUTE}] &`]: { transition: "none" },
      }}
    >
      <Toolbar disableGutters sx={{ px: "8px", alignItems: "center", gap: 1 }}>
        <Box p={1}>
          <Link to="/">
            <SvgIcon
              component={TSCLogoText}
              viewBox="0 0 67 39"
              sx={{ width: "67px", height: "39px", ml: "6px", mt: "1px" }}
            />
          </Link>
        </Box>
        <Box minWidth={{ xs: "8px", sm: "56px" }} flexGrow={1} />
        {genieLauncher}
        {quickaccessOptions.length > 0 && (
          <QuickAccessMenu options={quickaccessOptions} />
        )}
        <VerticalDivider />
        <Tooltip title="Profile & Account Settings">
          <IconButton onClick={handleOpenProfileMenu}>
            <Avatar
              src={user.photoURL ?? undefined}
              imgProps={{ referrerPolicy: "no-referrer" }}
              sx={{ width: 32, height: 32 }}
            />
          </IconButton>
        </Tooltip>
      </Toolbar>
      <ProfileMenu
        anchor={profileMenuAnchor}
        open={profileMenuOpen}
        onClose={handleCloseProfileMenu}
      />
    </AppBar>
  );
};

export default TopNav;

const buildQuickAccessOptions = (
  userPermissions: unknown,
  onClick: NavigateFunction
): QuickAccessOption[] => {
  const options: QuickAccessOption[] = [
    {
      title: "RBAC",
      icon: LockPersonIcon,
      onClick: () => onClick(`/rbac`),
    },
    {
      title: "Stakeholders",
      icon: PersonOutlineIcon,
      onClick: () =>
        onClick(
          `/stakeholders?${queryString.stringify(
            paths.STAKEHOLDERS.defaultQueryParams
          )}`
        ),
    },
    {
      title: "Scrapers",
      icon: HubOutlinedIcon,
      onClick: () => onClick(`/scrapers`),
    },
  ];

  // Platform admins only, like the built-ins the page reads (chat-service's
  // AdminGuard); the page itself then needs genie_knowledge_manage
  if (
    checkGlobalManagementRolePermission(
      userPermissions,
      OBJECT_TYPE.MANAGEMENT,
      OBJECT_NAME.READ
    )
  ) {
    options.push({
      title: "Genie knowledge",
      icon: AutoAwesomeOutlinedIcon,
      onClick: () => onClick(`/${paths.GENIE_KNOWLEDGE.path}`),
    });
    options.push({
      title: "GeniePlus Usage",
      icon: DataUsageIcon,
      onClick: () => onClick(`/${paths.GENIE_PLUS_USAGE.path}`),
    });
  }

  if (
    checkGlobalManagementRolePermission(
      userPermissions,
      OBJECT_TYPE.ORGANIZATION,
      OBJECT_NAME.CREATE
    )
  ) {
    options.push({
      title: "Organizations",
      icon: HomeOutlinedIcon,
      onClick: () => onClick("/organizations"),
      disabled: true,
    });
  }
  return options;
};
